/**
 * Base map catalogue (brief §5.1).
 *
 * Adding a base map is meant to be one entry in this array — id, label, tile
 * URL(s), attribution, max zoom, and which themes it's offered in. The UI and
 * the map both read from here, so nothing about a base map is defined twice.
 *
 * Google tiles are deliberately absent: pulling them directly violates their
 * terms. The extension point is kept (see `googlePlaceholder`) but not wired.
 */
import type { RasterLayerSpecification } from "maplibre-gl";

import type { Dictionary } from "../i18n/ru";

export type BasemapTheme = "light" | "dark" | "both";

export interface Basemap {
  id: string;
  /**
   * Key into `t.basemap`, not the label itself: this catalogue is a module
   * constant evaluated once at import, so a resolved string would keep the
   * language it was built with forever. The UI resolves it at render.
   */
  labelKey: keyof Dictionary["basemap"];
  /** One or more tile templates; multiple = overlay stack drawn in order. */
  tiles: string[];
  attribution: string;
  maxZoom: number;
  /** Which UI themes this base map is offered under. */
  themes: BasemapTheme;
  /**
   * Optional raster paint overrides applied to every tile layer of this base
   * map. Used to lift Dark Matter's near-black blacks to a charcoal so the
   * dark theme doesn't read as "too dark".
   */
  rasterPaint?: RasterLayerSpecification["paint"];
  /**
   * True for photographic imagery (satellite). Borders and labels switch to a
   * high-contrast white-on-dark-halo treatment over it, since the muted token
   * colors are unreadable against a dark, busy photo.
   */
  imagery?: boolean;
}

/**
 * CARTO raster basemaps require an API key since 2026 — unauthenticated tiles
 * come back watermarked "API KEY REQUIRED". The key is public by design (it is
 * visible in every tile request); free tier is 5M tiles/month.
 */
const CARTO_KEY =
  import.meta.env.VITE_CARTO_KEY ?? "cb1_2oq9_1_d1810ae6d09e4bbd2bb9640c";

const CARTO_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>';
const OSM_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const ESRI_ATTRIBUTION =
  "Esri, Maxar, Earthstar Geographics, and the GIS User Community";
const TOPO_ATTRIBUTION =
  '© <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA), © OpenStreetMap';

export const BASEMAPS: Basemap[] = [
  {
    id: "positron",
    labelKey: "positron",
    tiles: [
      `https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`,
      `https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`,
      `https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`,
    ],
    attribution: CARTO_ATTRIBUTION,
    maxZoom: 20,
    themes: "light",
  },
  {
    id: "dark-matter",
    labelKey: "darkMatter",
    tiles: [
      `https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`,
      `https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`,
      `https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`,
    ],
    attribution: CARTO_ATTRIBUTION,
    maxZoom: 20,
    themes: "dark",
    // Lift the blacks: maps pure black (0) to a soft charcoal so the base map
    // matches the softened dark surfaces instead of a flat near-black field.
    rasterPaint: { "raster-brightness-min": 0.16 },
  },
  {
    id: "satellite",
    labelKey: "satellite",
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: ESRI_ATTRIBUTION,
    maxZoom: 19,
    themes: "both",
    imagery: true,
  },
  {
    id: "satellite-labels",
    labelKey: "satelliteLabels",
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: ESRI_ATTRIBUTION,
    maxZoom: 19,
    themes: "both",
    imagery: true,
  },
  {
    id: "osm",
    labelKey: "osm",
    tiles: [
      "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
      "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
      "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
    ],
    attribution: OSM_ATTRIBUTION,
    maxZoom: 19,
    themes: "both",
  },
  {
    id: "topo",
    labelKey: "topo",
    tiles: ["https://a.tile.opentopomap.org/{z}/{x}/{y}.png"],
    attribution: TOPO_ATTRIBUTION,
    maxZoom: 17,
    themes: "both",
  },
];

/** The paired base map for a theme — brief §5.1: Positron ↔ Dark Matter. */
export const THEME_DEFAULT_BASEMAP: Record<"light" | "dark", string> = {
  light: "positron",
  dark: "dark-matter",
};

export function getBasemap(id: string): Basemap {
  return BASEMAPS.find((b) => b.id === id) ?? BASEMAPS[0];
}
